import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, Download, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

export function TellAFriend({ url, label = "Tell a Friend", listingTitle }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || window.location.href;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const textFriend = () => {
    const body = `${listingTitle ? `Check out "${listingTitle}" on Yard-skip: ` : "Check out this yard sale on Yard-skip: "}${shareUrl}`;
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent || "");
    const sep = isIOS ? "&" : "?";
    window.location.href = `sms:${sep}body=${encodeURIComponent(body)}`;
  };

  const emailFriend = () => {
    const subject = listingTitle ? `Check out "${listingTitle}" on Yard-skip` : "Check out this yard sale on Yard-skip";
    const body = `I found a sale I thought you'd like on Yard-skip:\n\n${listingTitle ? listingTitle + "\n" : ""}${shareUrl}\n\nHappy treasure hunting! 🏡`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Yard-skip", text: listingTitle || "Check out this yard sale on Yard-skip!", url: shareUrl }); }
      catch (err) { if (err?.name !== "AbortError") copy(); }
    } else copy();
  };

  const downloadQR = () => {
    const canvas = document.getElementById("ys-qr-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "yard-skip-qr.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full gap-2" data-testid="tell-a-friend-btn">
          <Share2 className="w-4 h-4" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="tell-a-friend-dialog">
        <DialogHeader><DialogTitle className="font-display">Share Yard-skip</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <QRCodeCanvas id="ys-qr-canvas" value={shareUrl} size={180} fgColor="#0284c7" level="M" includeMargin />
          </div>
          <p className="text-sm text-slate-500 text-center">Scan the QR code or copy the link to share.</p>
          <div className="flex w-full gap-2">
            <Button onClick={copy} variant="outline" className="flex-1 rounded-full gap-2" data-testid="copy-link-btn">
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={downloadQR} variant="outline" className="flex-1 rounded-full gap-2" data-testid="download-qr-btn">
              <Download className="w-4 h-4" /> QR
            </Button>
          </div>
          <div className="flex w-full gap-2">
            <Button onClick={textFriend} className="flex-1 rounded-full bg-ys-sky hover:bg-sky-700 gap-2" data-testid="sms-share-btn">
              <MessageSquare className="w-4 h-4" /> Text
            </Button>
            <Button onClick={emailFriend} className="flex-1 rounded-full bg-ys-sky hover:bg-sky-700 gap-2" data-testid="email-share-btn">
              <Mail className="w-4 h-4" /> Email
            </Button>
          </div>
          <Button onClick={nativeShare} variant="outline" className="w-full rounded-full gap-2" data-testid="forward-link-btn">
            <Share2 className="w-4 h-4" /> More ways to share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
